import { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { useCommander } from "./context";
import type { Command, CommandKey, ExecutionResult } from "../types";

// ============================================================================
// TYPES
// ============================================================================

export type CommandSource = "palette" | "shortcut" | "api";

export interface UseCommandOptions<TInput = any, TOutput = any> {
  // Execução
  source?: CommandSource;
  throwOnError?: boolean;
  timeout?: number;

  // Retry & Debounce
  retry?: number;
  retryDelay?: number | ((attempt: number) => number);
  debounce?: number;
  throttle?: number;

  // Input handling
  defaultInput?: Partial<TInput>;
  validateInput?: (input?: TInput) => boolean | string;
  transformInput?: (input?: TInput) => TInput;

  // Output handling
  transformOutput?: (output: TOutput) => TOutput;

  // Callbacks
  onSuccess?: (result: TOutput, input?: TInput) => void;
  onError?: (error: Error, input?: TInput) => void;
  onFinally?: (input?: TInput) => void;

  // Estado
  resetOnKeyChange?: boolean;
}

export interface CommandInvoker<TInput = any, TOutput = any> {
  // Estado do comando
  exists: boolean;
  isAvailable: boolean;
  isLoading: boolean;

  // Resultados
  lastResult: TOutput | null;
  lastError: Error | null;
  lastInput: TInput | null;
  lastExecution: Date | null;
  executionCount: number;

  // Comando completo
  command: Command<TInput, TOutput> | null;

  // Métodos de execução
  invoke: (
    input?: TInput,
    options?: Partial<UseCommandOptions<TInput, TOutput>>,
  ) => Promise<TOutput>;
  attempt: (
    input?: TInput,
    options?: Partial<UseCommandOptions<TInput, TOutput>>,
  ) => Promise<ExecutionResult<TOutput>>;
  execute: (
    input?: TInput,
    callbacks?: ExecuteCallbacks<TInput, TOutput>,
  ) => Promise<TOutput | null>;

  // Verificações
  canInvoke: (input?: TInput) => Promise<boolean>;
  validateInput: (input?: TInput) => boolean | string;

  // Estado
  reset: () => void;
  clearError: () => void;

  // Utilitários
  refresh: () => void;
}

export interface ExecuteCallbacks<TInput = any, TOutput = any> {
  onSuccess?: (result: TOutput, input?: TInput) => void;
  onError?: (error: Error, input?: TInput) => void;
  onFinally?: (input?: TInput) => void;
}

// ============================================================================
// UTILITIES
// ============================================================================

function createRetryDelay(delay: number | ((attempt: number) => number)) {
  return typeof delay === "function" ? delay : () => delay;
}

function debouncePromise<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  delay: number,
): T {
  let timeoutId: NodeJS.Timeout | null = null;
  let resolvePromise: ((value: any) => void) | null = null;
  let rejectPromise: ((error: any) => void) | null = null;

  return (async (...args: Parameters<T>) => {
    return new Promise<Awaited<ReturnType<T>>>((resolve, reject) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        if (rejectPromise) {
          rejectPromise(new Error("Debounced"));
        }
      }

      resolvePromise = resolve;
      rejectPromise = reject;

      timeoutId = setTimeout(async () => {
        try {
          const result = await fn(...args);
          if (resolvePromise) resolvePromise(result);
        } catch (error) {
          if (rejectPromise) rejectPromise(error);
        }
      }, delay);
    });
  }) as T;
}

function throttlePromise<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  delay: number,
): T {
  let lastExecution = 0;
  let pending: Promise<Awaited<ReturnType<T>>> | null = null;

  return (async (...args: Parameters<T>) => {
    const now = Date.now();

    if (pending) {
      return pending;
    }

    if (now - lastExecution < delay) {
      return new Promise<Awaited<ReturnType<T>>>((resolve, reject) => {
        setTimeout(
          async () => {
            try {
              const result = await fn(...args);
              resolve(result);
            } catch (error) {
              reject(error);
            }
            pending = null;
          },
          delay - (now - lastExecution),
        );
      });
    }

    lastExecution = now;
    pending = fn(...args);
    const result = await pending;
    pending = null;
    return result;
  }) as T;
}

// ============================================================================
// MAIN HOOK
// ============================================================================

/**
 * Hook para executar comandos existentes no Commander
 *
 * @param key - Chave do comando a ser executado
 * @param options - Opções de configuração
 * @returns CommandInvoker com estado e métodos de execução
 */
export function useCommand<TInput = any, TOutput = any>(
  key: CommandKey,
  options: UseCommandOptions<TInput, TOutput> = {},
): CommandInvoker<TInput, TOutput> {
  const commander = useCommander();

  // Estado interno
  const [isLoading, setIsLoading] = useState(false);
  const [lastResult, setLastResult] = useState<TOutput | null>(null);
  const [lastError, setLastError] = useState<Error | null>(null);
  const [lastInput, setLastInput] = useState<TInput | null>(null);
  const [lastExecution, setLastExecution] = useState<Date | null>(null);
  const [executionCount, setExecutionCount] = useState(0);
  const [isAvailable, setIsAvailable] = useState(false);

  // Refs para callbacks estáveis
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const {
    source = "api",
    throwOnError = true,
    timeout,
    retry = 0,
    retryDelay = 1000,
    debounce,
    throttle,
    defaultInput,
    validateInput,
    transformInput,
    transformOutput,
    onSuccess,
    onError,
    onFinally,
    resetOnKeyChange = true,
  } = options;

  // Estado do comando
  const command = commander.getCommand(key);
  const exists = !!command;

  // Reset quando key muda
  useEffect(() => {
    if (resetOnKeyChange) {
      setLastResult(null);
      setLastError(null);
      setLastInput(null);
      setLastExecution(null);
      setExecutionCount(0);
    }
  }, [key, resetOnKeyChange]);

  // Verifica disponibilidade
  useEffect(() => {
    let mounted = true;

    const checkAvailability = async () => {
      if (command) {
        const available = await commander.isCommandAvailable(command);
        if (mounted) {
          setIsAvailable(available);
        }
      } else {
        if (mounted) {
          setIsAvailable(false);
        }
      }
    };

    checkAvailability();

    // Re-verifica quando comandos mudam
    const listener = commander.listen("command:added", checkAvailability);
    const listener2 = commander.listen("command:removed", checkAvailability);

    return () => {
      mounted = false;
      commander.removeListener(listener);
      commander.removeListener(listener2);
    };
  }, [commander, command, key]);

  // Função de validação de input
  const inputValidator = useCallback(
    (input?: TInput): boolean | string => {
      if (validateInput) {
        return validateInput(input);
      }
      return true;
    },
    [validateInput],
  );

  // Verifica se pode invocar
  const canInvoke = useCallback(
    async (input?: TInput): Promise<boolean> => {
      if (!exists || !isAvailable) return false;

      const validation = inputValidator(input);
      if (validation !== true) return false;

      return true;
    },
    [exists, isAvailable, inputValidator],
  );

  // Função principal de execução com retry
  const executeWithRetry = useCallback(
    async (
      input?: TInput,
      overrideOptions: Partial<UseCommandOptions<TInput, TOutput>> = {},
    ): Promise<TOutput> => {
      const finalOptions = { ...optionsRef.current, ...overrideOptions };
      const retryDelayFn = createRetryDelay(
        finalOptions.retryDelay || retryDelay,
      );

      let lastError: Error;
      let currentInput = input;

      // Transform input se necessário
      if (finalOptions.transformInput || transformInput) {
        const transformer = finalOptions.transformInput || transformInput;
        currentInput = transformer!(currentInput);
      }

      // Merge com defaultInput
      if (defaultInput && currentInput) {
        currentInput = { ...defaultInput, ...currentInput } as TInput;
      } else if (defaultInput && !currentInput) {
        currentInput = defaultInput as TInput;
      }

      // Validar input
      const validation = inputValidator(currentInput);
      if (validation !== true) {
        throw new Error(
          typeof validation === "string" ? validation : "Invalid input",
        );
      }

      for (
        let attempt = 0;
        attempt <= (finalOptions.retry || retry);
        attempt++
      ) {
        try {
          const result = await commander.invoke<TOutput>(
            key,
            currentInput,
            finalOptions.source || source,
          );

          // Transform output se necessário
          let finalResult: TOutput = result;
          if (finalOptions.transformOutput || transformOutput) {
            const transformer = finalOptions.transformOutput || transformOutput;
            finalResult = transformer!(result);
          }

          return finalResult;
        } catch (error) {
          lastError = error instanceof Error ? error : new Error(String(error));

          if (attempt < (finalOptions.retry || retry)) {
            // Wait before retry
            await new Promise((resolve) =>
              setTimeout(resolve, retryDelayFn(attempt + 1)),
            );
          }
        }
      }

      throw lastError!;
    },
    [
      commander,
      key,
      source,
      retry,
      retryDelay,
      defaultInput,
      transformInput,
      transformOutput,
      inputValidator,
    ],
  );

  // Aplica debounce/throttle se necessário
  const processedExecute = useMemo(() => {
    let fn = executeWithRetry;

    if (debounce && debounce > 0) {
      fn = debouncePromise(fn, debounce);
    } else if (throttle && throttle > 0) {
      fn = throttlePromise(fn, throttle);
    }

    return fn;
  }, [executeWithRetry, debounce, throttle]);

  // Função invoke principal
  const invoke = useCallback(
    async (
      input?: TInput,
      overrideOptions: Partial<UseCommandOptions<TInput, TOutput>> = {},
    ): Promise<TOutput> => {
      const finalOptions = { ...optionsRef.current, ...overrideOptions };

      if (!(await canInvoke(input))) {
        const error = new Error(
          `Command "${key}" is not available or input is invalid`,
        );
        if (finalOptions.throwOnError !== false) {
          throw error;
        }
        return null as TOutput;
      }

      setIsLoading(true);
      setLastError(null);
      setLastInput(input || null);

      try {
        const result = await processedExecute(input, overrideOptions);

        setLastResult(result);
        setLastExecution(new Date());
        setExecutionCount((prev) => prev + 1);

        // Callback de sucesso
        const successCallback = finalOptions.onSuccess || onSuccess;
        if (successCallback) {
          successCallback(result, input);
        }

        return result;
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));

        setLastError(err);

        // Callback de erro
        const errorCallback = finalOptions.onError || onError;
        if (errorCallback) {
          errorCallback(err, input);
        }

        if (finalOptions.throwOnError !== false) {
          throw err;
        }

        return null as TOutput;
      } finally {
        setIsLoading(false);

        // Callback final
        const finallyCallback = finalOptions.onFinally || onFinally;
        if (finallyCallback) {
          finallyCallback(input);
        }
      }
    },
    [key, canInvoke, processedExecute, onSuccess, onError, onFinally],
  );

  // Função attempt (não lança erro)
  const attempt = useCallback(
    async (
      input?: TInput,
      overrideOptions: Partial<UseCommandOptions<TInput, TOutput>> = {},
    ): Promise<ExecutionResult<TOutput>> => {
      try {
        const result = await invoke(input, {
          ...overrideOptions,
          throwOnError: false,
        });
        return { success: true, result, command: key };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error : new Error(String(error)),
          command: key,
        };
      }
    },
    [invoke, key],
  );

  // Função execute com callbacks inline
  const execute = useCallback(
    async (
      input?: TInput,
      callbacks: ExecuteCallbacks<TInput, TOutput> = {},
    ): Promise<TOutput | null> => {
      try {
        const result = await invoke(input, {
          onSuccess: callbacks.onSuccess,
          onError: callbacks.onError,
          onFinally: callbacks.onFinally,
          throwOnError: false,
        });
        return result;
      } catch (error) {
        return null;
      }
    },
    [invoke],
  );

  // Funções de utilidade
  const reset = useCallback(() => {
    setLastResult(null);
    setLastError(null);
    setLastInput(null);
    setLastExecution(null);
    setExecutionCount(0);
  }, []);

  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  const refresh = useCallback(() => {
    // Force re-check availability
    if (command) {
      commander.isCommandAvailable(command).then(setIsAvailable);
    }
  }, [commander, command]);

  // Objeto CommandInvoker completo
  const invoker: CommandInvoker<TInput, TOutput> = useMemo(
    () => ({
      // Estado
      exists,
      isAvailable,
      isLoading,
      lastResult,
      lastError,
      lastInput,
      lastExecution,
      executionCount,
      command: command as Command<TInput, TOutput> | null,

      // Métodos
      invoke,
      attempt,
      execute,
      canInvoke,
      validateInput: inputValidator,
      reset,
      clearError,
      refresh,
    }),
    [
      exists,
      isAvailable,
      isLoading,
      lastResult,
      lastError,
      lastInput,
      lastExecution,
      executionCount,
      command,
      invoke,
      attempt,
      execute,
      canInvoke,
      inputValidator,
      reset,
      clearError,
      refresh,
    ],
  );

  // Sempre retorna o CommandInvoker
  return invoker;
}

// ============================================================================
// EXPORTS
// ============================================================================

export default useCommand;

// Types are exported where they are declared
